import { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { BoxModal } from '../../../boxModal';
import Asterisk from '../../../../shared/components/asterisk/asterisk';
import IntlCurrencyInput from 'react-intl-currency-input';
import { BeatLoader } from 'react-spinners';
import { useAuth } from '../../../../hooks/useAuth';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { LimitOperationTypes } from '../../../../enums/accountLimitsEnums';
import { FaPaperclip } from 'react-icons/fa6';
import { toast } from 'react-toastify';

const RequestLimitPixModal = ({
  isOpenModal,
  setIsOpenModal,
  loading,
  handleRequestLimit,
  limitIncreaseRequests,
  originalLimits = { daily: 0, overnight: 0, transaction: 0, titular: 0 },
}) => {
  const [limits, setLimits] = useState({
    daily: 0,
    overnight: 0,
    transaction: 0,
    titular: 0,
  });
  const [hasChangedLimits, setHasChangedLimits] = useState(false);

  const [justification, setJustification] = useState('');
  const [file, setFile] = useState(null);
  const [hasFile, setHasFile] = useState(false);
  const [showFileInput, setShowFileInput] = useState(true);
  const [blockedLimits, setBlockedLimits] = useState(new Set());
  const { user } = useAuth();

  const currencyConfig = {
    locale: 'pt-BR',
    formats: {
      number: {
        BRL: {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      },
    },
  };

  const limitsMap = {
    daily: {
      label: 'Limite diário',
      key: 'daily',
    },
    overnight: {
      label: 'Limite noturno',
      key: 'overnight',
    },
    transaction: {
      label: 'Limite de transação',
      key: 'transaction',
    },
    titular: {
      label: 'Limite para mesmo titular',
      key: 'titular',
    },
  }

  const justificationOptions = [
    { key: 0, label: 'Negócios' },
    { key: 1, label: 'Renda' },
    { key: 2, label: 'Emergência' },
    { key: 3, label: 'Educação' },
    { key: 4, label: 'Saúde' },
    { key: 5, label: 'Viagem' },
    { key: 6, label: 'Renovação' },
    { key: 7, label: 'Outros' }
  ]

  const requestTypeMap = {
    '0': 'daily',
    '3': 'overnight',
    '4': 'transaction',
    '5': 'titular',
  };

  const handleCurrencyChange = (event, value, maskedValue, name) => {
    setLimits((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    if (!justification) {
      toast.error('Justificativa obrigatória');
      return;
    }

    if (!hasChangedLimits) {
      toast.error('Limite deve ser diferente do atual');
      return;
    }

    if( !hasFile && !file ) {
      toast.error('Comprovante de renda obrigatório');
      return;
    }

    handleRequestLimit({
      limits,
      justification,
      file,
    });
  };

  const checkHasChangedLimits = () => {
    const hasChanged = Object.keys(limits).some(key => {
      return limits[key] !== originalLimits[key];
    });
    setHasChangedLimits(hasChanged);
  };

  useEffect(() => {
    checkHasChangedLimits();
  }, [limits]);

  useEffect(() => {
    if (user?.compRenda?.length) {
      setShowFileInput(false);
      setHasFile(true);
    }
  }, [user]);

  useEffect(() => {
    if (limitIncreaseRequests?.length > 0) {
      const pendingPixRequests = limitIncreaseRequests.filter(
        req => Number(req.requestedOperationType) === LimitOperationTypes.PIX
      );

      setBlockedLimits(new Set(pendingPixRequests.map(req => {
        return requestTypeMap[req.requestedType];
      })));
    }
  }, [limitIncreaseRequests]);

  return (
    <ReactModal
      isOpen={isOpenModal}
      className={'change-modal'}
      overlayClassName={'overlay-change-password-modal'}
    >
      <BoxModal
        maxWidth={700}
        title="Solicitar aumento de limite"
        close={() => setIsOpenModal(false)}
      >
        <div className='d-flex flex-column gap-3 pix-limit-content'>
          <p className="m-0 limit-alert-message">
            <AiOutlineInfoCircle size={24} color="var(--primary-color)" />
            <span>
              Você pode solicitar <strong>aumento de limite</strong> apenas <strong>a cada 90 dias</strong>.<br />
              Preencha os campos abaixo para enviar sua solicitação.
            </span>
          </p>


          <div className="input-field-group" style={{ display: 'grid', gap: 16 }}>
            {
              Object.keys(limitsMap).map((limit) => {
                const originalValue = Number(originalLimits[limit]);
                const isBlocked = blockedLimits.has(limit);

                return (
                  <div className="pix-limit-input-group" >
                    <label style={{ fontWeight: 600 }}>
                      {limitsMap[limit].label} <Asterisk />
                    </label>
                    <IntlCurrencyInput
                      currency="BRL"
                      config={currencyConfig}
                      name={limit}
                      value={limits[limit]}
                      defaultValue={originalValue}
                      onChange={(e, v, mv) => {
                        if (isBlocked) return;
                        if (v < originalValue) {
                          setLimits(prev => ({
                            ...prev,
                            [limit]: originalValue
                          }));
                          return;
                        }
                        handleCurrencyChange(e, v, mv, limit);
                      }}
                      disabled={isBlocked}
                      style={{
                        borderRadius: 6,
                        border: '1px solid #ccc',
                        padding: '8px 12px',
                        fontSize: '14px',
                        width: '100%'
                      }}
                    />
                    {!isBlocked &&
                      <small style={{ color: '#666', marginTop: 4, display: 'block' }}>
                        Atual: {originalValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </small>
                    }

                    {isBlocked && (
                      <small style={{ color: 'red', marginTop: 4, display: 'block' }}>
                        Solicitação pendente ou solicitação realizada nos últimos 90 dias
                      </small>
                    )}
                  </div>
                );
              })
            }
          </div>

          {
            hasFile && 
            <>
            <div className="mb-3 p-3 border rounded">
              <label className="form-label">Comprovante de Renda *</label>
              <div className="d-flex align-items-center mb-2">
                <FaPaperclip className="me-2 text-secondary" />
                <span>Você já possui um comprovante de renda</span>
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => {
                  if (user?.compRenda) {
                    window.open(user.compRenda[0].documentFile, '_blank');
                  }
                }}>
                  Visualizar Arquivo Atual
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowFileInput(true)}>
                  Substituir Comprovante
                </button>
              </div>
            </div>
            </>
          }
          {
            showFileInput &&
              <div className="file-upload-dropzone">
                <label>{hasFile ? "Substituir Comprovante" : ("Comprovante de Renda")}</label>
                <div
                  className="dropzone"
                  onClick={() => document.getElementById('fileInput').click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const droppedFile = e.dataTransfer.files[0];
                    setFile(droppedFile);
                  }}
                >
                  {file ? (
                    <span>{file.name}</span>
                  ) : (
                    <span>📁 Clique para selecionar ou arraste um documento aqui</span>
                  )}
                </div>
                <input
                  id="fileInput"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(e) => setFile(e.target.files[0])}
                  style={{ display: 'none' }}
                />
              </div>
          }

          <div className="pix-limit-input-group" >
            <label>Justificativa <Asterisk /></label>
            <select
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              required
            >
              <option value="" hidden>Selecione</option>
              {
                justificationOptions.map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
            </select>
          </div>

          <div className="request-limit-button-area" style={{ marginTop: 32 }}>
            <button
              type="button"
              className="request-limit-confirm-button"
              onClick={handleSubmit}
              disabled={loading || !hasChangedLimits || !justification}
              style={{
                background: 'var(--primary-color)',
                color: '#fff',
                padding: '12px 24px',
                borderRadius: 8,
                border: 'none',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                width: '100%'
              }}
            >
              {loading ? <BeatLoader color="white" size={10} /> : 'Confirmar Solicitação'}
            </button>

          </div>
        </div>
      </BoxModal>
    </ReactModal>
  );
};

export default RequestLimitPixModal;
